import {
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NodeProps, Position, useEdges, useNodes, useReactFlow } from 'reactflow';

import {
  Box,
  Button,
  Divider,
  List,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from '@mui/material';
import {
  CaretDown,
  CircleWavyQuestion,
  Copy,
  Faders,
  PencilSimple,
  Plus,
  TextAlignLeft,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import '../../assets/styles/animatedBorder.scss';
import { spacesQueries } from '../../queries/spaces';
import { vectorQueries } from '../../queries/vector';
import { VectorService } from '../../service/VectorService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { useNodeFieldAutoSave } from '../../util/autosave/useNodeFieldAutoSave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { arrayDataToText } from '../../util/dataToText';
import { executeErrorHandle } from '../../util/executeErrorHandle';
import { isActiveEdge } from '../../util/findActiveHandle';
import { getModelMaxTokens } from '../../util/modelMaxTokens';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import LinesReferenceModal, {
  LinesReference,
  ReferencesHeader,
} from '../LargeMemory/LinesReferenceModal';
import { ListConditionItem } from '../LargeMemory/ListConditionItem';
import PodSelect from '../LargeMemory/PodSelect/PodSelect';
import SettingsModal from '../LargeMemory/SettingsModal/SettingsModal';
import { HelpModal } from '../LargeMemory/HelpModal';
import { UploadNewContentSection } from '../LargeMemory/UploadNewContentSection';
import {
  buildConnectedContent,
  filterContentEdges,
  isSameContentList,
  readPersistedContent,
  type ConnectedContentItem,
} from '../LargeMemory/connectedContentUtils';
import { transformFiltersToPineconeQuery } from '../LargeMemory/filterToPinecone';
import {
  createFilterFromLabel,
  parseFilterInput,
} from '../LargeMemory/filterUtils';
import type { Filter } from '../LargeMemory/types';
import ConnectedTags from '../Test/ConnectedTags';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';
import PreviewModal from '../UI/PreviewModal/PreviewModal';
import { VectorMemoryModal } from '../UI/VectorMemoryModal';

const ModeSwitch = styled(Switch)(() => ({
  cursor: 'progress',

  '& .MuiSwitch-switchBase': {
    color: '#3650DE',

    '& + .MuiSwitch-track': {
      backgroundColor: '#3650DE',
      opacity: 0.6,
    },

    '&.Mui-checked': {
      color: '#3650DE',

      '& + .MuiSwitch-track': {
        backgroundColor: '#3650DE',
        opacity: 0.6,
      },
    },

    '&.Mui-disabled': {
      color: '#98A2B3',

      '& + .MuiSwitch-track': {
        backgroundColor: '#98A2B3',
        opacity: 0.6,
      },
    },
  },
}));

const LargeMemory = ({ id, data, type }: NodeProps) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const { user } = useAuthStore(state => state);
  const { setSaving, setSchema, flowId, spaceId } = useFlowsStore(
    state => state,
  );

  const { setNodes: setNodeState } = useReactFlow();
  const edges = useEdges();
  const allNodes = useNodes();
  const objectCallerData = useMemo(
    () => [...(user?.spaceObjects || []), ...(user?.objects || [])],
    [user?.spaceObjects, user?.objects],
  );

  const { data: userNamespaces } = useQuery({
    ...vectorQueries.allNamespaces(),
    enabled: !spaceId,
  });
  const { data: space } = useQuery({
    ...spacesQueries.list(spaceId),
    enabled: !!spaceId,
  });

  const namespaces = spaceId ? space?.data.namespaces : userNamespaces?.data;

  const hasQueryData = data?.queryData?.length > 0;

  const [values, setValues] = useState<{
    searchResult: string;
    model: string;
    systemAi: string;
    topK: number;
    linesReferences: LinesReference[] | null;
    referencesHeader: ReferencesHeader | null;
    searchType: string;
    returnRaw: boolean;
  }>({
    searchResult: '',
    model: 'gpt-5.4',
    systemAi: 'OpenAI',
    topK: 10,
    linesReferences: null,
    referencesHeader: null,
    searchType: 'similarity',
    returnRaw: false,
  });

  const [queryValue, setQueryValue] = useNodeFieldAutoSave<string>({
    externalValue: data?.query ?? '',
    fieldName: 'query',
    nodeId: id,
    flowId,
    edges,
    setNodeState,
    setSaving,
    setSchema,
    debounceMs: 300,
  });

  const [whoIsChanging, setWhoIsChanging] = useState<{
    value: any;
    name: string;
  }>({
    value: '',
    name: '',
  });
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  const [nodeLoading, setNodeLoading] = useState(false);
  const [processedContentKeys, setProcessedContentKeys] = useState<Set<string>>(
    new Set(),
  );
  const [promptCollapsed, setPromptCollapsed] = useState(false);
  const [labelFilterCollapsed, setLabelFilterCollapsed] = useState(true);
  const [executeCounter, setExecuteCounter] = useState(0);
  const [selectVector, setSelectVector] = useState<any>(data?.nameSpace);
  const [loaders, setLoaders] = useState({
    search: false,
    upload: false,
  });
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [openVectorUploadModal, setOpenVectorUploadModal] = useState<{
    open: boolean;
    namespaceId?: string;
  }>({
    open: false,
  });
  const [openLinesReferenceModal, setLinesReferenceModal] = useState(false);
  const [labels, setLabels] = useState<Array<string>>([]);
  const [connectedContent, setConnectedContent] = useState<
    ConnectedContentItem[]
  >(readPersistedContent(data?.connectedContent));
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  const [filterInput, setFilterInput] = useState('');
  const [copyPrompt, setCopyPrompt] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [filterHelpOpen, setFilterHelpOpen] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);
  const [pendingReturnRaw, setPendingReturnRaw] = useState<boolean | null>(
    null,
  );

  const getContentKey = (item: { id: string; value?: string }) =>
    `${item.id}::${item.value || ''}`;

  const hasPendingUpload = connectedContent.some(
    item => item.value && !processedContentKeys.has(getContentKey(item)),
  );

  useEffect(() => {
    if (pendingReturnRaw !== null && data?.returnRaw === pendingReturnRaw) {
      setPendingReturnRaw(null);
    }
  }, [data?.returnRaw, pendingReturnRaw]);

  const isSavingReturnRaw = pendingReturnRaw !== null;

  const searchFile = async () => {
    setLoaders({
      ...loaders,
      search: true,
    });

    setNodeLoading(true);

    try {
      if (!selectVector) {
        throw new Error('Please select a vector');
      }

      let prompt = queryValue;

      if (data?.queryData?.length > 0) {
        prompt = arrayDataToText(data?.queryData);
      }

      const selectedNamespace = namespaces?.find(
        namespace => namespace.id === data.nameSpace.id,
      );

      if (!selectedNamespace) {
        return;
      }

      const pineconeFilter = transformFiltersToPineconeQuery(activeFilters);

      const formatUploadContent = (): string[] => {
        const contentToUse = connectedContent
          .filter(item => item.value)
          .map(item => item.value.trim());

        if (!contentToUse || contentToUse.length === 0) {
          return [];
        }

        return contentToUse;
      };

      const uploadNewContent = formatUploadContent();

      const uploadMetadata = data?.applyFiltersToUpload
        ? activeFilters
            .filter(
              f =>
                (f.operator === 'equals' || f.operator === 'contains') &&
                f.value?.trim(),
            )
            .map(f => ({ name: f.label, value: f.value }))
        : [];

      const result = await VectorService.search({
        body: {
          prompt,
          model: values.model,
          topK: +values.topK,
          maxTokenModel: getModelMaxTokens(values.model, true),
          isNERSearch: values.searchType === 'similarity-ner',
          embeddingModelName: selectedNamespace.embeddingModelName,
          returnRaw: !!data.returnRaw,
          filters: pineconeFilter,
          uploadNewContent,
          uploadMetadata,
          applyDimensionsToUploads: !!data?.applyDimensionsToUpload,
        },
        params: {
          nameSpace: selectVector.name,
          id: selectVector.id,
        },
      });

      const resultText = data?.returnRaw
        ? [
            result?.data?.referencesHeader?.pageContent,
            ...(result?.data?.linesWhereQuestionWasFound ?? []).map(
              (line: any) => `${line.lines} \n ${line.pageContent}`,
            ),
          ]
            .filter(Boolean)
            .join('\n ')
        : result.data.text;

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        changeType: 'execute',
        objectCallerData: [
          ...(user?.spaceObjects || []),
          ...(user?.objects || []),
        ],

        flowTriggerData: checkExecuteTrigger(data, id),
        flowId,
        objectData: {
          text: {
            result: resultText,
            linesWhereQuestionWasFound: result?.data.linesWhereQuestionWasFound,
            referencesHeader: result?.data.referencesHeader,
            text: result?.data?.text,
          },
        },
        setSchema,
      });

      setExecuteCounter(0);
      setProcessedContentKeys(
        new Set(connectedContent.map(item => getContentKey(item))),
      );

      setTimeout(() => {
        setHasToExecute(true);
      }, 1500);

      if (data?.clearFiltersOnExecution) {
        setActiveFilters([]);
        setLabels([]);
      }
    } catch (error: any) {
      executeErrorHandle(
        executeCounter,
        setExecuteCounter,
        setHasToExecute,
        data,
        edges,
        setNodeState,
        setSaving,
        id,
        flowId,
        setSchema,
        setNotification,
        error,
        [...(user?.spaceObjects || []), ...(user?.objects || [])],
      );
      setNotification({
        type: 'error',
        message: error.message || 'Something went wrong',
      });
      setNodeLoading(false);
    } finally {
      setNodeLoading(false);
    }
  };

  const debounceTimeoutRef = useRef<any>(null);

  useEffect(() => {
    if (data?.returnRaw === undefined) {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: { returnRaw: true },
        setSchema,
      });
    }
  }, []);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          searchFile();
          setHasToExecute(false);
        }, 1500);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, values, selectVector, data?.canceled]);

  const persistConnectedContent = useCallback(
    (next: ConnectedContentItem[]) => {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: { connectedContent: next },
        objectCallerData,
        setSchema,
      });
    },
    [edges, setNodeState, id, setSaving, flowId, objectCallerData, setSchema],
  );

  useEffect(() => {
    const contentEdges = filterContentEdges(edges, id);
    const persisted = readPersistedContent(data?.connectedContent);
    const next = buildConnectedContent(contentEdges, allNodes, persisted);
    if (isSameContentList(next, connectedContent)) return;
    setConnectedContent(next);
    persistConnectedContent(next);
  }, [edges, id, allNodes, data?.connectedContent, persistConnectedContent]);

  useDebounceEffect(
    () => {
      if (!isChangingDirectly) {
        const stored: LinesReference[] =
          data?.text?.linesWhereQuestionWasFound ?? [];
        let linesReferences = stored;
        let referencesHeader: ReferencesHeader | null =
          data?.text?.referencesHeader ?? null;

        if (
          !referencesHeader &&
          stored[0]?.lines === '' &&
          stored[0]?.pageContent?.startsWith('Dimensions Found:')
        ) {
          referencesHeader = {
            pageContent: stored[0].pageContent,
            dimensions: [],
          };
          linesReferences = stored.slice(1);
        }

        setValues({
          ...values,
          searchResult: data.text ? data.text.result : '',
          linesReferences,
          referencesHeader,
          model: data?.model ?? 'gpt-5.4',
          topK: data?.topK ?? 10,
          systemAi: data?.systemAi ?? 'OpenAI',
          searchType: data?.searchType ?? 'similarity',
          returnRaw: data?.returnRaw ?? false,
        });
      }
    },
    [data, isChangingDirectly, namespaces],
    300,
  );

  const onChange = (e: any) => {
    const name = e.target.name;

    if (name === 'query') {
      setQueryValue(e.target.value);
      return;
    }

    setValues({
      ...values,
      [name]: e.target.value,
    });

    setIsChangingDirectly(true);
    setWhoIsChanging({
      name: name,
      value: e.target.value,
    });
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);

        let newObject = {};
        if (whoIsChanging?.name === 'returnRaw') {
          const linesReferences =
            values.linesReferences
              ?.map((line: any) => `${line.lines} \n ${line.pageContent}`)
              ?.join('\n ') ?? '';

          if (whoIsChanging) {
            newObject = {
              text: {
                linesWhereQuestionWasFound: values.linesReferences,
                result: linesReferences,
              },
            };
          }
        }

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
            ...newObject,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  const onChangeMemoryPod = useCallback(
    (e: any) => {
      const selectedVector = namespaces?.find(
        namespace => namespace.name === e?.target?.value,
      );
      setSelectVector(selectedVector);
      setIsChangingDirectly(true);
      setWhoIsChanging({
        name: 'nameSpace',
        value: selectedVector,
      });
    },
    [namespaces],
  );

  const onChangeOnExecute = (e: any) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        clearOnExecution: e.target.checked,
      },
      setSchema,
    });
  };

  const onChangeClearFiltersOnExecute = (e: any) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        clearFiltersOnExecution: e.target.checked,
      },
      setSchema,
    });
  };

  const handleRun = (run: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      setSaving,
      id,
      flowId,
      objectData: {
        run,
      },
      setSchema,
    });
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  const handleOpenVectorModal = ({
    open,
    namespaceId,
  }: {
    open: boolean;
    namespaceId?: string;
  }) => {
    setOpenVectorUploadModal(() => ({
      open,
      namespaceId,
    }));
  };

  const onCopyPrompt = () => {
    if (queryValue) {
      navigator.clipboard.writeText(queryValue);
      setCopyPrompt(true);
      setTimeout(() => {
        setCopyPrompt(false);
      }, 2000);
    }
  };

  const handleLabelsChange = (
    _: MouseEvent<HTMLElement>,
    newLabels: Array<string>,
  ) => {
    setLabels(newLabels);

    const addedLabels = newLabels.filter(label => !labels.includes(label));
    const removedLabels = labels.filter(label => !newLabels.includes(label));

    if (addedLabels.length > 0) {
      const newFilters = addedLabels.map(label => {
        const labelValues = getLabelValues(label);
        const value = labelValues.length > 0 ? labelValues[0] : '';
        return {
          ...createFilterFromLabel(label),
          value,
        };
      });
      setActiveFilters(prev => [...prev, ...newFilters]);
    }

    if (removedLabels.length > 0) {
      setActiveFilters(prev =>
        prev.filter(filter => !removedLabels.includes(filter.label)),
      );
    }
  };

  const handleGenerateFilter = () => {
    const newFilter = parseFilterInput(filterInput);
    if (!newFilter) {
      setNotification({
        type: 'error',
        message:
          'Invalid filter format. Use: "label, operator value" — e.g. "subject, contains test".',
      });
      return;
    }

    setActiveFilters([...activeFilters, newFilter]);

    if (
      availableLabels.includes(newFilter.label) &&
      !labels.includes(newFilter.label)
    ) {
      setLabels([...labels, newFilter.label]);
    }

    setFilterInput('');
  };

  const handleFilterOperatorChange = useCallback(
    (id: string, operator: string) => {
      setActiveFilters(prev =>
        prev.map(filter =>
          filter.id === id ? { ...filter, operator } : filter,
        ),
      );
    },
    [],
  );

  const handleFilterValueChange = useCallback((id: string, value: string) => {
    setActiveFilters(prev =>
      prev.map(filter => (filter.id === id ? { ...filter, value } : filter)),
    );
  }, []);

  const handleDeleteFilter = useCallback(
    (id: string) => {
      const filterToDelete = activeFilters.find(filter => filter.id === id);
      if (!filterToDelete) return;
      setActiveFilters(prev => prev.filter(filter => filter.id !== id));
      setLabels(prev => prev.filter(label => label !== filterToDelete.label));
    },
    [activeFilters],
  );

  const closeSettingsModal = useCallback(() => setSettingsModalOpen(false), []);

  const availableLabels = useMemo(() => {
    if (!selectVector || !selectVector.id || !namespaces) return [];

    const selectedNamespace = namespaces.find(
      namespace => namespace.id === selectVector.id,
    );

    if (!selectedNamespace) return [];

    if (!selectedNamespace.files || selectedNamespace.files.length === 0) {
      return [];
    }

    const allMetadataNames = new Set<string>();
    selectedNamespace.files.forEach(file => {
      if (file.metadatas && Array.isArray(file.metadatas)) {
        file.metadatas.forEach(metadata => {
          if (metadata.name) {
            allMetadataNames.add(metadata.name);
          }
        });
      }
    });

    return Array.from(allMetadataNames).sort();
  }, [selectVector, namespaces]);

  const getLabelValues = useMemo(() => {
    if (!selectVector || !selectVector.id || !namespaces) {
      return () => [];
    }

    const selectedNamespace = namespaces.find(
      namespace => namespace.id === selectVector.id,
    );

    if (!selectedNamespace || !selectedNamespace.files) {
      return () => [];
    }

    return (labelName: string): string[] => {
      const values = new Set<string>();
      selectedNamespace.files.forEach(file => {
        if (file.metadatas && Array.isArray(file.metadatas)) {
          file.metadatas.forEach(metadata => {
            if (metadata.name === labelName && metadata.value) {
              values.add(metadata.value);
            }
          });
        }
      });
      return Array.from(values);
    };
  }, [selectVector, namespaces]);

  return (
    <Box className={nodeLoading ? 'node' : ''}>
      <Node
        type={type}
        id={id}
        data={data}
        btnText='Search in memory pod'
        handleRun={handleRun}
        isLoading={nodeLoading}
        edges={edges}
        onSubmit={searchFile}
      >
        <Box display={'flex'}>
          <Box
            width={510}
            p={'16px'}
            borderRight={'1px solid #EAECF0'}
            display='flex'
            flexDirection='column'
            gap='1rem'
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              <InputHandle
                activeColor={findColor?.color}
                handleId={'textInput'}
                left={-26}
                top={'75%'}
                position={Position.Left}
                type='target'
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  '& svg': { cursor: 'pointer' },
                }}
              >
                <Typography
                  fontSize={16}
                  color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                  fontWeight={600}
                >
                  Memory Pod
                </Typography>

                <Box>
                  <IconButtonTooltip
                    onClick={() =>
                      setOpenVectorUploadModal({
                        open: true,
                      })
                    }
                    title={'Create new Memory Pod'}
                  >
                    <Plus fontSize={20} color='#667085' cursor='pointer' />
                  </IconButtonTooltip>

                  <IconButtonTooltip
                    onClick={() =>
                      setOpenVectorUploadModal({
                        open: true,
                        namespaceId: data.nameSpace.id,
                      })
                    }
                    title={'Edit active Memory Pod'}
                  >
                    <PencilSimple
                      fontSize={20}
                      color='#667085'
                      cursor='pointer'
                    />
                  </IconButtonTooltip>
                </Box>
              </Box>

              <PodSelect
                onChangeMemoryPod={onChangeMemoryPod}
                namespaces={namespaces}
                selectVector={selectVector}
              />
            </Box>

            <Stack spacing='1rem'>
              <Stack spacing='0.5rem' position='relative'>
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  position='relative'
                >
                  <InputHandle
                    handleId='textQuery'
                    activeColor={findColor?.color}
                    isActive={isActiveEdge(edges, id, 'textQuery', 'target')}
                    left={-26}
                    top={'50%'}
                    position={Position.Left}
                    type='target'
                  />

                  <Stack direction='row' alignItems='center' spacing='1rem'>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing='0.625rem'
                    >
                      <CaretDown
                        onClick={evt => {
                          evt.stopPropagation();
                          setPromptCollapsed(prev => !prev);
                        }}
                        size={20}
                        style={{
                          rotate: promptCollapsed ? '-90deg' : '0deg',
                          transition: 'rotate 150ms ease-in',
                          cursor: 'pointer',
                        }}
                      />

                      <Typography fontWeight={600}>Prompt</Typography>
                    </Stack>

                    <Stack direction='row' alignItems='center' gap='0.5rem'>
                      <IconButtonTooltip
                        title='Preview'
                        onClick={() => setOpenPreviewModal(true)}
                      >
                        <TextAlignLeft size={16} color='#98A2B3' />
                      </IconButtonTooltip>

                      {!hasQueryData && (
                        <IconButtonTooltip
                          title={copyPrompt ? 'Text Copied' : 'Copy text'}
                          placement='top'
                          onClick={onCopyPrompt}
                          componentProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: copyPrompt
                                  ? '#80C683'
                                  : undefined,
                                color: copyPrompt ? 'white' : undefined,
                              },
                            },
                            arrow: {
                              sx: {
                                color: copyPrompt ? '#80C683' : undefined,
                              },
                            },
                          }}
                        >
                          <Copy color='#98A2B3' size={16} />
                        </IconButtonTooltip>
                      )}
                    </Stack>
                  </Stack>

                  <Stack direction='row' alignItems='center' spacing='0.25rem'>
                    <Typography fontSize='0.875rem'>Raw</Typography>

                    <ModeSwitch
                      size='small'
                      disabled={isSavingReturnRaw}
                      checked={data?.returnRaw === false}
                      onChange={() => {
                        const next = !data?.returnRaw;
                        setPendingReturnRaw(next);
                        onChange({
                          target: {
                            name: 'returnRaw',
                            value: next,
                          },
                        });
                      }}
                    />

                    <Typography fontSize='0.875rem'>Ai Mode</Typography>
                  </Stack>
                </Stack>

                {!promptCollapsed ? (
                  <>
                    {!hasQueryData ? (
                      <TextField
                        className='nodrag'
                        name='query'
                        value={queryValue}
                        onChange={onChange}
                        placeholder='Prompt search here'
                        fullWidth
                      />
                    ) : (
                      <ConnectedTags
                        dataConnected={data?.queryData}
                        onChange={onChange}
                        getFieldName={() => 'query'}
                        name='query'
                        nodeId={id}
                        handleId='textQuery'
                        maxVisible={3}
                      />
                    )}
                  </>
                ) : null}
              </Stack>

              <Stack spacing='0.5rem'>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Stack direction='row' alignItems='center' spacing='0.625rem'>
                    <CaretDown
                      onClick={evt => {
                        evt.stopPropagation();
                        setLabelFilterCollapsed(prev => !prev);
                      }}
                      size={20}
                      style={{
                        rotate: labelFilterCollapsed ? '-90deg' : '0deg',
                        transition: 'rotate 150ms ease-in',
                        cursor: 'pointer',
                      }}
                    />

                    <Typography fontSize='0.875rem' fontWeight={600}>
                      Filter Retrieval by Label
                    </Typography>
                  </Stack>
                  <IconButtonTooltip
                    title='Help'
                    onClick={() => setFilterHelpOpen(true)}
                  >
                    <CircleWavyQuestion size={16} color='#98A2B3' />
                  </IconButtonTooltip>
                </Stack>

                {!labelFilterCollapsed && (
                  <>
                    <Stack spacing='0.5rem'>
                      <Typography fontSize='0.75rem'>
                        Labels in this memory pod
                      </Typography>

                      {availableLabels.length === 0 ? (
                        <Box
                          sx={{
                            padding: '1rem',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '0.5rem',
                            border: '1px solid #EAECF0',
                          }}
                        >
                          <Typography
                            fontSize='0.875rem'
                            color='#667085'
                            textAlign='center'
                          >
                            No labels available. Please add labels to your
                            memory pod files to use filtering.
                          </Typography>
                        </Box>
                      ) : (
                        <ToggleButtonGroup
                          color='primary'
                          sx={{ flexWrap: 'wrap', gap: '0.5rem' }}
                          value={labels}
                          onChange={handleLabelsChange}
                        >
                          {availableLabels.map(label => (
                            <ToggleButton
                              key={label}
                              value={label}
                              size='small'
                              sx={{
                                marginLeft: 0,
                                textTransform: 'capitalize',
                                borderRadius: '0.5rem !important',
                                borderWidth: '1px !important',
                                borderStyle: 'solid !important',
                                borderColor: '#F9FAFB !important',
                                borderLeftWidth: '1px !important',
                                borderLeftStyle: 'solid !important',
                                '&.Mui-selected': {
                                  borderColor: '#3650DE !important',
                                },
                                backgroundColor: '#F9FAFB',
                              }}
                            >
                              {label.split('_').join(' ')}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      )}
                    </Stack>

                    <Divider />

                    <Stack spacing='0.5rem'>
                      <Typography fontSize='0.75rem'>
                        Type your filter here
                      </Typography>

                      <Stack
                        direction='row'
                        spacing='0.5rem'
                        alignItems='center'
                      >
                        <TextField
                          placeholder='label, subject contains'
                          value={filterInput}
                          onChange={e => setFilterInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              handleGenerateFilter();
                            }
                          }}
                          fullWidth
                          size='small'
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem',
                            },
                          }}
                        />

                        <Button
                          variant='outlined'
                          onClick={handleGenerateFilter}
                          disabled={nodeLoading}
                          sx={{
                            backgroundColor: '#3650DE',
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            padding: '0.5rem 1rem',
                            '&:hover': {
                              backgroundColor: '#2E42B8',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#D0D5DD',
                              color: '#fff',
                            },
                          }}
                        >
                          Generate
                        </Button>
                      </Stack>
                    </Stack>

                    <Stack spacing='0.5rem'>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Typography fontSize='0.75rem'>
                          Active Filters
                        </Typography>

                        <Stack
                          direction='row'
                          alignItems='center'
                          spacing='0.25rem'
                        >
                          <Switch
                            size='small'
                            checked={data?.clearFiltersOnExecution || false}
                            onChange={onChangeClearFiltersOnExecute}
                          />

                          <Typography fontSize='0.875rem'>
                            clear on run
                          </Typography>
                        </Stack>
                      </Stack>

                      {activeFilters.length === 0 ? (
                        <Box
                          sx={{
                            padding: '8px',
                            borderRadius: '0.5rem',
                            border: '1px solid #D0D5DD',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            fontSize='0.875rem'
                            color='#667085'
                            textAlign='center'
                            lineHeight='1.66'
                          >
                            No filters yet
                          </Typography>
                        </Box>
                      ) : (
                        <List
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                            padding: 0,
                          }}
                        >
                          {activeFilters.map(filter => (
                            <ListConditionItem
                              key={filter.id}
                              filter={filter}
                              onOperatorChange={handleFilterOperatorChange}
                              onValueChange={handleFilterValueChange}
                              onDelete={handleDeleteFilter}
                            />
                          ))}
                        </List>
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>

            <Divider />

            <UploadNewContentSection
              id={id}
              findColor={findColor}
              connectedContent={connectedContent}
              onContentChange={setConnectedContent}
              edges={edges}
              isProcessing={nodeLoading && hasPendingUpload}
              isNodeLoading={nodeLoading}
              applyFilters={data?.applyFiltersToUpload || false}
              applyDimensions={data?.applyDimensionsToUpload || false}
              onApplyFiltersChange={value => {
                nodeDataAutoSaveDynamic({
                  newEdges: edges,
                  setNodeState,
                  id,
                  setSaving,
                  flowId,
                  objectData: { applyFiltersToUpload: value },
                  objectCallerData,
                  setSchema,
                });
              }}
              onApplyDimensionsChange={value => {
                nodeDataAutoSaveDynamic({
                  newEdges: edges,
                  setNodeState,
                  id,
                  setSaving,
                  flowId,
                  objectData: { applyDimensionsToUpload: value },
                  objectCallerData,
                  setSchema,
                });
              }}
            />

            <Divider />

            {data?.placement !== 'Output' && (
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Box
                  bgcolor={user?.theme === 'dark' ? '#101828' : '#F9FAFB'}
                  borderRadius={'4px'}
                  sx={{
                    display: 'flex',
                    padding: '8px',
                    gap: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSettingsModalOpen(true)}
                >
                  <Faders size={18} color='#667085' />
                  <Typography fontSize={'12px'} color='#667085' variant='h6'>
                    Open AI: {values.model}
                  </Typography>

                  <CaretDown size={'16px'} color='#667085' />
                </Box>
              </Box>
            )}

            <SettingsModal
              onHide={closeSettingsModal}
              show={settingsModalOpen}
              formData={{ ...values, query: queryValue }}
              onChange={onChange}
            />
          </Box>

          {/* output side */}
          <Box p={'16px 24px 16px 16px'} height={'100%'}>
            <OutputTextarea
              nodeId={id}
              maxOutputHeight={500}
              previewIndex={data.previewIndex}
              value={data?.text?.result}
              activeHandle={isActiveEdge(edges, id, 'output', 'source')}
              placement={data?.placement}
              labelName={'Output'}
              onChangeOnExecute={onChangeOnExecute}
              hasClearOnExecute={true}
              clearOnExecution={data.clearOnExecution || false}
              findColor={findColor}
              nodeLabel={data?.label}
              style={{
                flex: 1,
              }}
              textareaHeight={'100%'}
            />

            <Box>
              {(values?.linesReferences?.length ?? 0) > 0 && (
                <Button
                  onClick={() => setLinesReferenceModal(true)}
                  variant='text'
                  sx={{ mt: '16px' }}
                >
                  Show references {values?.linesReferences?.length}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <VectorMemoryModal
          namespaceId={openVectorUploadModal.namespaceId}
          spaceId={spaceId}
          open={openVectorUploadModal.open}
          onOpenChange={handleOpenVectorModal}
        />

        <LinesReferenceModal
          openLinesReferenceModal={openLinesReferenceModal}
          setLinesReferenceModal={setLinesReferenceModal}
          linesReferences={values.linesReferences}
          referencesHeader={values.referencesHeader}
        />

        <PreviewModal
          show={openPreviewModal}
          onHide={() => setOpenPreviewModal(false)}
          data={
            hasQueryData
              ? {
                  data: (data?.queryData || []).map((item: any) => {
                    const itemText =
                      typeof item?.text === 'object'
                        ? item?.text?.text || item?.text || ''
                        : item?.text || '';

                    return {
                      text: itemText,
                      label: item?.label || 'Text Box',
                      edit: false,
                    };
                  }),
                  labelName: 'Prompt',
                  nodeLabel: data?.label,
                }
              : {
                  value: queryValue || '',
                  labelName: 'Prompt',
                  readOnly: false,
                  name: 'query',
                  nodeLabel: data?.label,
                }
          }
          onChange={onChange}
          name='query'
        />

        <HelpModal
          open={filterHelpOpen}
          onClose={() => setFilterHelpOpen(false)}
          title='Filter Retrieval by Label'
          description={`Use labels to narrow down the vector search scope. Add filter rules to find only content within files that match specific metadata criteria.

Supported operators:
• Contains / Does Not Contain: for text search within metadata
• Equals / Not Equals: for exact matches
• Greater Than / Less Than: for numeric metadata

When you apply filters, the search will only return results from files matching all active filter rules.`}
        />
      </Node>
    </Box>
  );
};

export default memo(LargeMemory);
